import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Clock, User, BookOpen, Target } from "lucide-react";
import coverPipeline from "@/assets/cover-gestao-de-pipeline.jpg";

const GestaoDePipeline = () => {
  const navigate = useNavigate();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline:
      "Gestão de Pipeline: Como Gerenciar o Funil de Vendas e Prever Receita com Segurança",
    description:
      "Como gerenciar pipeline na prática: etapas do funil de vendas por decisão do cliente, higiene do CRM para vendas, cobertura de pipeline e reunião semanal de revisão.",
    image: "https://bianchimbusiness.com.br/cover-gestao-de-pipeline.jpg",
    datePublished: "2026-09-05",
    author: { "@type": "Person", name: "Junior Bianchim" },
    publisher: {
      "@type": "Organization",
      name: "Bianchim Business",
      logo: { "@type": "ImageObject", url: "https://bianchimbusiness.com.br/logo.png" },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/gestao-de-pipeline",
    },
  };

  const etapas = [
    {
      etapa: "1. Lead qualificado",
      criterio: "Perfil bate com o ICP e houve contato com resposta do cliente",
      prob: "10%",
    },
    {
      etapa: "2. Reunião de diagnóstico realizada",
      criterio: "Dor confirmada pelo cliente, com impacto declarado em número ou tempo",
      prob: "25%",
    },
    {
      etapa: "3. Solução validada",
      criterio: "Cliente concordou que a sua solução resolve e conhece a faixa de investimento",
      prob: "45%",
    },
    {
      etapa: "4. Proposta apresentada",
      criterio: "Proposta apresentada ao decisor (não só enviada por e-mail)",
      prob: "60%",
    },
    {
      etapa: "5. Negociação",
      criterio: "Condições em discussão, com data-alvo de decisão combinada",
      prob: "80%",
    },
    {
      etapa: "6. Fechada ganha / perdida",
      criterio: "Contrato assinado ou motivo de perda registrado",
      prob: "100% / 0%",
    },
  ];

  const sinais = [
    {
      sinal: "Oportunidade parada há mais que o dobro do tempo médio da etapa",
      acao: "Marcar como estagnada e definir uma ação com data ou mover para perdida",
    },
    {
      sinal: "Negócio sem próxima atividade agendada",
      acao: "Sem próximo passo com data, não é oportunidade — é esperança",
    },
    {
      sinal: "Valor e data de fechamento iguais há três meses",
      acao: "Revisar com o cliente; previsão que nunca muda é previsão inventada",
    },
    {
      sinal: "Etapa avançada sem contato com o decisor",
      acao: "Voltar a oportunidade de etapa até mapear quem assina",
    },
    {
      sinal: "Mais de 40% do pipeline concentrado em um único vendedor ou cliente",
      acao: "Risco de previsão: distribuir esforço de prospecção com urgência",
    },
  ];

  return (
    <>
      <SEOHead
        title="Gestão de Pipeline: Como Gerenciar o Funil de Vendas em 2026 | Bianchim Business"
        description="Como gerenciar pipeline de vendas na prática: etapas do funil de vendas, higiene e uso real do CRM para vendas, cobertura de pipeline, previsão de receita e revisão semanal."
        keywords="gestão de pipeline, como gerenciar pipeline, funil de vendas, CRM para vendas, previsão de vendas, forecast comercial, pipeline de vendas B2B"
        canonicalUrl="https://bianchimbusiness.com.br/posts/gestao-de-pipeline"
        ogType="article"
        articlePublishedTime="2026-09-05"
        articleAuthor="Junior Bianchim"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[46vh] sm:h-[58vh] relative overflow-hidden">
                <img
                  src={coverPipeline}
                  alt="Junior Bianchim apresentando o funil de vendas e o pipeline no CRM em um painel digital"
                  className="w-full h-full object-cover"
                  width={1600}
                  height={912}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-28 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    Gestão Comercial
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Gestão de pipeline:{" "}
                    <span className="text-gradient-brand">
                      como gerenciar o funil de vendas e prever receita sem chute
                    </span>
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" /> <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" /> <span>12 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" /> <span>05 Set 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Conteúdo */}
            <section className="container mx-auto px-4 py-12">
              <div className="max-w-4xl mx-auto space-y-8 text-base sm:text-lg leading-relaxed text-muted-foreground">
                <p>
                  Toda semana eu abro o CRM de alguma empresa e encontro a mesma cena: duzentas
                  oportunidades abertas, metade delas sem nenhuma atividade nos últimos trinta dias,
                  previsões de fechamento no passado e um total de pipeline que, se fosse verdade,
                  faria a empresa dobrar de tamanho no trimestre. O gestor olha aquele número e
                  dorme tranquilo. Um mês depois, a meta não fecha e ninguém sabe explicar o que
                  aconteceu.
                </p>
                <p>
                  Gestão de pipeline não é preencher CRM. É manter uma fotografia honesta de onde o
                  dinheiro está, quanto dele é real e o que precisa acontecer nesta semana para ele
                  entrar. Aqui eu mostro como gerenciar pipeline de verdade: como desenhar as etapas
                  do funil de vendas, como usar o CRM para vendas sem transformar o vendedor em
                  digitador, como calcular cobertura e previsão, e como conduzir a revisão semanal
                  que separa negócio real de conversa animada.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  1. Etapas do funil precisam descrever a decisão do cliente, não a tarefa do
                  vendedor
                </h2>
                <p>
                  O erro mais caro na gestão de pipeline aparece na primeira configuração do CRM.
                  Etapas chamadas "ligação feita", "proposta enviada" e "follow-up" descrevem o que o
                  vendedor fez — e o vendedor sempre faz alguma coisa. Por isso o funil enche e nada
                  fecha. Uma etapa só existe quando algo mudou na cabeça do cliente, e cada avanço
                  precisa de um critério de passagem verificável.
                </p>

                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-foreground">
                      <tr>
                        <th className="p-3 font-semibold">Etapa</th>
                        <th className="p-3 font-semibold">Critério de passagem</th>
                        <th className="p-3 font-semibold">Probabilidade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {etapas.map((e) => (
                        <tr key={e.etapa} className="border-t border-border align-top">
                          <td className="p-3 font-medium text-foreground">{e.etapa}</td>
                          <td className="p-3">{e.criterio}</td>
                          <td className="p-3">{e.prob}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p>
                  As probabilidades acima são um ponto de partida: depois de três meses de histórico,
                  substitua por conversões reais da sua operação. Se hoje você não tem esse
                  histórico, comece medindo o básico descrito no guia de{" "}
                  <Link to="/posts/metricas-e-kpis-de-vendas" className="text-primary underline">
                    métricas e KPIs de vendas
                  </Link>{" "}
                  — sem taxa de conversão por etapa, qualquer previsão é palpite com planilha.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  2. CRM para vendas: o mínimo que precisa estar preenchido
                </h2>
                <p>
                  CRM não é ferramenta de controle do gestor, é a memória da negociação. Quando o
                  vendedor entende isso, o preenchimento deixa de ser burocracia. Mas para isso o
                  campo obrigatório precisa ser curto: valor, etapa, data provável de decisão,
                  próxima atividade agendada e quem é o decisor. Cinco campos. Qualquer coisa além
                  disso vira campo em branco ou preenchido com mentira.
                </p>
                <p>
                  Dois deles carregam quase toda a saúde do funil. O primeiro é a próxima atividade
                  com data: oportunidade sem próximo passo agendado não é oportunidade, é lembrança.
                  O segundo é o decisor: negócio que chegou à proposta sem que o vendedor tenha
                  falado com quem assina tem uma taxa de perda brutal, e o CRM precisa expor isso
                  antes da previsão ser feita.
                </p>
                <p>
                  Regra prática de disciplina: o que não está no CRM não existe na reunião semanal.
                  Se o vendedor diz "esse cliente já confirmou verbalmente" e o sistema não mostra
                  nada, o negócio não entra na previsão. É duro nas duas primeiras semanas e resolve
                  o problema para sempre.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  3. Higiene de pipeline: os sinais de que o funil está mentindo
                </h2>
                <p>
                  Pipeline inflado é pior que pipeline vazio, porque impede a decisão. Enquanto o
                  gestor acredita que tem três vezes a meta em negociação, ninguém prospecta. Uma vez
                  por semana, rode a limpeza olhando para estes cinco sinais.
                </p>

                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-foreground">
                      <tr>
                        <th className="p-3 font-semibold">Sinal de alerta</th>
                        <th className="p-3 font-semibold">O que fazer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sinais.map((s) => (
                        <tr key={s.sinal} className="border-t border-border align-top">
                          <td className="p-3 font-medium text-foreground">{s.sinal}</td>
                          <td className="p-3">{s.acao}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p>
                  Perder um negócio no CRM não é fracasso; é informação. O motivo de perda registrado
                  com honestidade — preço, timing, concorrente, sem orçamento, sem decisor — é a
                  matéria-prima que melhora a qualificação lá no começo do funil.
                </p>

                <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 to-accent/10 p-6 sm:p-8 mt-10">
                  <div className="flex items-center gap-2 mb-3 text-primary">
                    <Target className="w-5 h-5" />
                    <span className="font-semibold">Pipeline travado quase sempre é conversa travada</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
                    Negócio que não avança de etapa é vendedor que não fez a pergunta certa
                  </h3>
                  <p className="mb-5">
                    A maior parte das oportunidades estagnadas não morreu por preço: morreu porque
                    ninguém confirmou a dor, mapeou o decisor ou pediu a decisão. No Vendas Trainer,
                    o vendedor treina exatamente essas conversas com personas de IA no perfil dos
                    seus clientes — o que enrola, o que só fala de preço, o que some depois da
                    proposta — e recebe nota de 0 a 100 com feedback de descoberta, contorno e
                    fechamento. Funil limpo começa em conversa bem conduzida.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => navigate("/auth?mode=signup")}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Criar conta grátis e destravar o pipeline
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/calculadora-roi")}>
                      Calcular o ROI do treinamento
                    </Button>
                  </div>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  4. Cobertura e previsão: a conta que evita o susto no fim do mês
                </h2>
                <p>
                  Cobertura de pipeline é a relação entre o valor em aberto e a meta do período. Se a
                  sua taxa de conversão de oportunidade para venda é de 25%, você precisa de quatro
                  vezes a meta em pipeline para ter chance real de bater — cobertura 4x. Com meta de
                  R$ 300 mil no mês e conversão de 25%, o funil precisa carregar R$ 1,2 milhão em
                  negócios com data de decisão dentro do mês. Não vale somar oportunidade com
                  previsão para o trimestre seguinte: cobertura se calcula por período de
                  fechamento.
                </p>
                <p>
                  Para a previsão, use duas visões lado a lado. A ponderada multiplica cada negócio
                  pela probabilidade da etapa (R$ 100 mil em negociação a 80% = R$ 80 mil). A
                  comprometida lista apenas os negócios que o vendedor assume com data e decisor
                  confirmados. Quando as duas ficam muito distantes, o problema está na disciplina de
                  etapa, não no mercado. E se a cobertura está baixa, nenhuma técnica de fechamento
                  resolve — o buraco é de topo de funil, e a saída é o que descrevi no guia de{" "}
                  <Link to="/posts/prospeccao-b2b" className="text-primary underline">
                    prospecção B2B
                  </Link>
                  .
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  5. A reunião semanal de pipeline que realmente move negócio
                </h2>
                <p>
                  Revisão de pipeline não é sessão de cobrança nem relatório falado. Uma hora,
                  agenda fixa, e a regra de ouro: não se discute o negócio inteiro, discute-se o
                  próximo passo. Comece pelos números do time (cobertura, negócios estagnados,
                  previsão comprometida), passe para os cinco maiores negócios do período e feche com
                  os travados há mais tempo.
                </p>
                <p>
                  As perguntas do gestor mudam tudo. Em vez de "como está esse cliente?", pergunte:
                  qual critério de passagem foi cumprido para ele estar nessa etapa; quem, com nome,
                  assina o contrato; o que o cliente disse com as próprias palavras sobre o impacto
                  do problema; e qual é a próxima atividade agendada e a data. Quando o vendedor não
                  responde, o negócio volta de etapa na hora, na frente de todos. Duas semanas
                  fazendo isso e o pipeline da empresa inteira fica honesto.
                </p>
                <p>
                  Essa linguagem precisa estar documentada para não depender do humor do gestor. É
                  papel do{" "}
                  <Link to="/posts/playbook-de-vendas" className="text-primary underline">
                    playbook de vendas
                  </Link>{" "}
                  definir os critérios de cada etapa, e do desenho do{" "}
                  <Link to="/posts/funil-vendas-2026" className="text-primary underline">
                    funil de vendas
                  </Link>{" "}
                  garantir que o topo alimente o meio na velocidade certa.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  Conclusão: pipeline honesto vale mais que pipeline grande
                </h2>
                <p>
                  Gerenciar pipeline é aceitar perder negócio no CRM antes de perder no caixa. Um
                  funil com quarenta oportunidades reais, com decisor mapeado e próximo passo
                  marcado, prevê receita melhor que duzentas oportunidades de mentira. Comece nesta
                  semana pelo mais simples: reescreva as etapas por decisão do cliente, apague ou
                  marque como perdido tudo que está parado há mais de sessenta dias e exija próxima
                  atividade agendada em cada negócio aberto. O número vai encolher, e pela primeira
                  vez você vai poder confiar nele.
                </p>
              </div>
            </section>

            <PageNavigation />
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default GestaoDePipeline;
